import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  getStoreScheduleConfigApi,
  minutesToTime,
  saveStoreScheduleConfigApi,
  SHIFT_LABELS,
  StoreScheduleConfig,
  timeToMinutes,
  WEEKDAY_LABELS,
} from "../api/scheduleApi";

type Props = {
  token: string;
  storeId: string;
  onSaved?: () => void;
};

/**
 * Horário de funcionamento por dia da semana, os dois turnos e as regras de
 * escala de uma loja: aba "Horário e turnos" da ficha da loja. Era o modal
 * `StoreScheduleEditorModal` de DP → Cadastros; a posição da loja saiu daqui e
 * ganhou aba própria (plan-notificacoes-whatsapp, 22/10/2026). O salvar não
 * manda a posição, que fica como está.
 */
export default function StoreScheduleForm({ token, storeId, onSaved }: Props) {
  const [config, setConfig] = useState<StoreScheduleConfig | null>(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setConfig(null);
    setFailed(false);
    getStoreScheduleConfigApi(token, storeId)
      .then((c) => active && setConfig(c))
      .catch((error: any) => {
        if (!active) return;
        setFailed(true);
        toast.error(error?.message || "Erro ao carregar o horário da loja.");
      });
    return () => {
      active = false;
    };
  }, [token, storeId]);

  if (failed) return <div className="dpEmpty">Não foi possível carregar o horário da loja.</div>;
  if (!config) return <div className="dpEmpty">Carregando...</div>;

  const setHour = (weekday: number, patch: Partial<StoreScheduleConfig["openingHours"][number]>) =>
    setConfig({ ...config, openingHours: config.openingHours.map((h) => (h.weekday === weekday ? { ...h, ...patch } : h)) });

  const setShift = (code: string, patch: Partial<StoreScheduleConfig["shifts"][number]>) =>
    setConfig({ ...config, shifts: config.shifts.map((s) => (s.code === code ? { ...s, ...patch } : s)) });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const h of config.openingHours) {
      if (h.isOpen && (h.openMinute === null || h.closeMinute === null)) {
        return toast.error(`Informe abertura e fechamento de ${WEEKDAY_LABELS[h.weekday].toLowerCase()}.`);
      }
      // Fechamento antes da abertura vale: a loja fecha no dia seguinte (ex.: 09:00 às 01:30).
      if (h.isOpen && h.openMinute === h.closeMinute) {
        return toast.error(`O fechamento de ${WEEKDAY_LABELS[h.weekday].toLowerCase()} deve ser diferente da abertura.`);
      }
    }
    setSaving(true);
    try {
      const saved = await saveStoreScheduleConfigApi(token, storeId, {
        opensOnHolidays: config.opensOnHolidays,
        sundayOffEveryWeeks: config.sundayOffEveryWeeks,
        noDayOffWeekdays: config.noDayOffWeekdays,
        openingHours: config.openingHours,
        shifts: config.shifts,
      });
      setConfig(saved);
      toast.success("Horário da loja salvo.");
      onSaved?.();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o horário da loja.");
    } finally {
      setSaving(false);
    }
  };

  const sundayOpen = config.openingHours.find((h) => h.weekday === 0)?.isOpen ?? false;

  return (
    <form className="storeTab__form" onSubmit={save}>
      {!config.configured ? (
        <p className="dpNotice dpNotice--warning">Loja ainda não configurada: abaixo, o horário padrão. Ajuste e salve.</p>
      ) : null}

      <h3 className="dpSection__title">Funcionamento</h3>
      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Dia</th>
              <th>Abre?</th>
              <th>Abertura</th>
              <th>Fechamento</th>
            </tr>
          </thead>
          <tbody>
            {config.openingHours.map((h) => (
              <tr key={h.weekday}>
                <td>{WEEKDAY_LABELS[h.weekday]}</td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${WEEKDAY_LABELS[h.weekday]} aberta`}
                    checked={h.isOpen}
                    onChange={(e) =>
                      setHour(h.weekday, {
                        isOpen: e.target.checked,
                        openMinute: e.target.checked ? h.openMinute ?? 480 : h.openMinute,
                        closeMinute: e.target.checked ? h.closeMinute ?? 1320 : h.closeMinute,
                      })
                    }
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    className="dpInput"
                    value={minutesToTime(h.openMinute)}
                    onChange={(e) => setHour(h.weekday, { openMinute: timeToMinutes(e.target.value) })}
                    disabled={saving || !h.isOpen}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    className="dpInput"
                    value={minutesToTime(h.closeMinute)}
                    onChange={(e) => setHour(h.weekday, { closeMinute: timeToMinutes(e.target.value) })}
                    disabled={saving || !h.isOpen}
                  />
                  {h.isOpen && h.openMinute !== null && h.closeMinute !== null && h.closeMinute < h.openMinute ? (
                    <span className="dpFormHint">Fecha no dia seguinte</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dpFormGrid" style={{ marginTop: 12 }}>
        <label className="dpCheckboxRow" style={{ gridColumn: "1 / -1" }}>
          <input
            type="checkbox"
            checked={config.opensOnHolidays}
            onChange={(e) => setConfig({ ...config, opensOnHolidays: e.target.checked })}
            disabled={saving}
          />{" "}
          Abre nos feriados (nacionais, estaduais da UF e municipais da loja)
        </label>
        <div className="dpFormGroup">
          <label htmlFor="sundayOff">Domingo de folga a cada (semanas)</label>
          <input
            id="sundayOff"
            type="number"
            min={1}
            max={7}
            className="dpInput"
            value={config.sundayOffEveryWeeks}
            onChange={(e) => setConfig({ ...config, sundayOffEveryWeeks: Number(e.target.value) || 1 })}
            disabled={saving || !sundayOpen}
          />
          <span className="dpFormHint">
            {sundayOpen
              ? "Cada um folga ao menos um domingo no mês; a lei do comércio exige um a cada 3 semanas."
              : "A loja não abre domingo: o domingo é sempre o descanso."}
          </span>
        </div>
        <fieldset className="dpFormGroup" style={{ gridColumn: "1 / -1", border: 0, padding: 0, margin: 0 }}>
          <legend style={{ fontWeight: 600, marginBottom: 6 }}>Dias sem folga</legend>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
            {WEEKDAY_LABELS.map((label, weekday) => (
              <label key={label} className="dpCheckboxRow">
                <input
                  type="checkbox"
                  checked={config.noDayOffWeekdays.includes(weekday)}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      noDayOffWeekdays: e.target.checked
                        ? [...config.noDayOffWeekdays, weekday].sort()
                        : config.noDayOffWeekdays.filter((d) => d !== weekday),
                    })
                  }
                  disabled={saving}
                />{" "}
                {label}
              </label>
            ))}
          </div>
          <span className="dpFormHint">Nesses dias ninguém folga: a sugestão não usa, e o sistema recusa folga fixa, folga à mão e troca.</span>
        </fieldset>
      </div>

      <h3 className="dpSection__title" style={{ marginTop: 16 }}>
        Turnos
      </h3>
      <div className="dpTableWrapper">
        <table className="dpTable">
          <thead>
            <tr>
              <th>Turno</th>
              <th>Ativo</th>
              <th>Início</th>
              <th>Fim</th>
              <th>Intervalo (min)</th>
            </tr>
          </thead>
          <tbody>
            {config.shifts.map((s) => (
              <tr key={s.code}>
                <td>{SHIFT_LABELS[s.code]}</td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Turno ${SHIFT_LABELS[s.code]} ativo`}
                    checked={s.isActive}
                    onChange={(e) => setShift(s.code, { isActive: e.target.checked })}
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    className="dpInput"
                    value={minutesToTime(s.startMinute)}
                    onChange={(e) => setShift(s.code, { startMinute: timeToMinutes(e.target.value) ?? s.startMinute })}
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="time"
                    className="dpInput"
                    value={minutesToTime(s.endMinute)}
                    onChange={(e) => setShift(s.code, { endMinute: timeToMinutes(e.target.value) ?? s.endMinute })}
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    max={240}
                    className="dpInput"
                    value={s.breakMinutes}
                    onChange={(e) => setShift(s.code, { breakMinutes: Number(e.target.value) || 0 })}
                    disabled={saving}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="dpFormHint">
        Padrão sugerido para 44h em 6 dias: 7h20 de trabalho + 1h de intervalo (manhã 08:00–16:20, noite 13:40–22:00). Fim antes do
        início = turno que passa da meia-noite (ex.: 13:40–01:30).
      </p>

      <div className="storeTab__actions">
        <button type="submit" className="ecoBtn ecoBtn--primary" disabled={saving}>
          {saving ? "Salvando..." : "Salvar horário e turnos"}
        </button>
      </div>
    </form>
  );
}
